/** What the channel itself says: ad accounts + campaigns, social followers, affiliates. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeDollarSign, CalendarDays, Megaphone, Plug, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Empty, Pill, Section, Spark, dueLabel, num, timeAgo, usd } from "@/features/command/ui";
import { AFFILIATE_CHANNEL, AFFILIATE_KIND, platformLabel } from "../shared";
import type { Campaign, ConnectedAccount, DetailResponse, SourceAffiliate, SyncState } from "../types";

function SyncButton({ onSync, syncing, at }: { onSync: () => void; syncing: boolean; at?: string | null }) {
  return (
    <Button variant="ghost" size="sm" onClick={onSync} loading={syncing} leftIcon={<RefreshCw className="h-3.5 w-3.5" />} title={at ? `Last synced ${new Date(at).toLocaleString()}` : "Never synced"}>
      {at ? `Synced ${timeAgo(at)}` : "Sync now"}
    </Button>
  );
}

function AccountChip({ a, followers }: { a: ConnectedAccount; followers?: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-xs",
      a.needs_reconnect ? "border-amber-500/30 bg-amber-500/5 text-amber-200" : "border-zinc-800 bg-zinc-900/50 text-zinc-200",
    )}>
      {a.avatar ? <img src={a.avatar} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-semibold text-zinc-300">{platformLabel(a.platform).charAt(0)}</span>}
      <span className="font-medium">{platformLabel(a.platform)}</span>
      {a.username && <span className="text-zinc-500">{a.username.startsWith("@") ? a.username : `@${a.username}`}</span>}
      {followers && a.followers != null && <span className="tabular-nums text-zinc-400">{num(a.followers)}</span>}
      {a.needs_reconnect && <span className="flex items-center gap-1 text-amber-300"><AlertTriangle className="h-3 w-3" />Reconnect</span>}
    </span>
  );
}

// ─── Ads ─────────────────────────────────────────────────────────────────────

export function AdsSection({ ads, campaigns, onSync, syncing }: {
  ads: DetailResponse["ads"]; campaigns: Campaign[]; onSync: () => void; syncing: boolean;
}) {
  const accounts = ads?.accounts ?? [];
  const sync: SyncState | null = ads?.sync ?? null;
  const sum = useMemo(() => campaigns.reduce((t, c) => ({
    s7: t.s7 + (c.d7?.spend ?? 0), s30: t.s30 + (c.d30?.spend ?? 0), clicks: t.clicks + (c.d30?.clicks ?? 0), conv: t.conv + (c.d30?.conversions ?? 0),
  }), { s7: 0, s30: 0, clicks: 0, conv: 0 }), [campaigns]);
  const sorted = useMemo(() => [...campaigns].sort((a, b) => (b.d30?.spend ?? 0) - (a.d30?.spend ?? 0)), [campaigns]);

  return (
    <Section title="Ads" icon={Megaphone} count={campaigns.length} action={<SyncButton onSync={onSync} syncing={syncing} at={sync?.at} />} bodyClassName="p-0">
      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-2 border-b border-zinc-800/70 px-4 py-3">
          {accounts.map((a) => <AccountChip key={a.id} a={a} />)}
        </div>
      )}
      {sync?.errors && sync.errors.length > 0 && (
        <div className="flex items-start gap-2 border-b border-zinc-800/70 bg-amber-500/5 px-4 py-2 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Last sync had problems: {sync.errors.slice(0, 2).join(" · ")}</span>
        </div>
      )}
      {campaigns.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-zinc-800/70 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-2 font-semibold">Campaign</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-2 py-2 text-right font-semibold">Spend 7d</th>
                <th className="px-2 py-2 text-right font-semibold">Spend 30d</th>
                <th className="px-2 py-2 text-right font-semibold">Clicks</th>
                <th className="px-2 py-2 text-right font-semibold">Results</th>
                <th className="px-4 py-2 text-right font-semibold">Per click</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {sorted.map((c) => {
                const d = c.d30;
                const cpc = d && d.clicks > 0 ? d.spend / d.clicks : null;
                const live = /active|enabled|running/i.test(c.status ?? "");
                return (
                  <tr key={`${c.platform}:${c.campaign_id}`} className="text-zinc-300">
                    <td className="max-w-[260px] px-4 py-2.5">
                      <div className="truncate font-medium text-zinc-100" title={c.name ?? undefined}>{c.name || c.campaign_id}</div>
                      <div className="text-[11px] text-zinc-500">{platformLabel(c.platform)}{c.ads ? ` · ${num(c.ads)} ads` : ""}</div>
                    </td>
                    <td className="px-2 py-2.5"><Pill color={live ? "emerald" : null}>{c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase() : "Unknown"}</Pill></td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{usd(c.d7?.spend ?? 0)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-zinc-100">{usd(d?.spend ?? 0)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{num(d?.clicks ?? 0)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{num(d?.conversions ?? 0)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{cpc == null ? "–" : usd(cpc, { cents: true })}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-800/70 text-zinc-100">
                <td className="px-4 py-2.5 text-xs font-semibold" colSpan={2}>All campaigns</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{usd(sum.s7)}</td>
                <td className="px-2 py-2.5 text-right font-semibold tabular-nums">{usd(sum.s30)}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{num(sum.clicks)}</td>
                <td className="px-2 py-2.5 text-right tabular-nums">{num(sum.conv)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{sum.clicks ? usd(sum.s30 / sum.clicks, { cents: true }) : "–"}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : accounts.length === 0 ? (
        <Empty icon={Plug} title="No ad accounts connected" action={<Button size="sm" variant="secondary" onClick={onSync} loading={syncing} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Sync</Button>}>
          Connect your ad accounts in SyncSuite → Apps → Social &amp; ads, then press Sync. Spend, clicks and results land here.
        </Empty>
      ) : (
        <Empty icon={Megaphone} title="No campaigns synced yet" action={<Button size="sm" variant="secondary" onClick={onSync} loading={syncing} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Sync now</Button>}>
          Your ad accounts are connected. Press Sync to pull the last 30 days of campaigns.
        </Empty>
      )}
    </Section>
  );
}

// ─── Followers ───────────────────────────────────────────────────────────────

export function FollowersSection({ social, onSync, syncing, syncAt }: {
  social: NonNullable<DetailResponse["social"]>; onSync: () => void; syncing: boolean; syncAt?: string | null;
}) {
  const rows = useMemo(() => {
    const growth = social.growth.accounts;
    const seen = new Set<string>();
    const out = social.accounts.map((a) => {
      const g = growth.find((x) => x.account_id === a.id);
      seen.add(a.id);
      return { a, followers: g?.followers ?? a.followers ?? 0, delta: g?.delta ?? 0, series: g?.series.map((p) => p.followers) ?? [] };
    });
    // Accounts with history but no longer connected still count.
    for (const g of growth) {
      if (seen.has(g.account_id)) continue;
      out.push({
        a: { id: g.account_id, platform: g.platform ?? "", raw_platform: g.platform ?? "", username: g.username ?? "", ads: false, needs_reconnect: false, followers: g.followers, avatar: null },
        followers: g.followers, delta: g.delta, series: g.series.map((p) => p.followers),
      });
    }
    return out.sort((x, y) => y.followers - x.followers);
  }, [social]);
  const total = rows.reduce((t, r) => t + r.followers, 0);
  const delta = rows.reduce((t, r) => t + r.delta, 0);

  return (
    <Section
      title="Followers"
      icon={Sparkles}
      count={rows.length}
      action={
        <div className="flex items-center gap-1">
          <SyncButton onSync={onSync} syncing={syncing} at={syncAt} />
          <Link to="/content" className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-medium text-white hover:bg-indigo-500">
            <CalendarDays className="h-3.5 w-3.5" /> Plan posts
          </Link>
        </div>
      }
      bodyClassName="p-0"
    >
      {rows.length === 0 ? (
        <Empty icon={Plug} title="No social accounts connected" action={<Button size="sm" variant="secondary" onClick={onSync} loading={syncing} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Sync</Button>}>
          Connect your social accounts in SyncSuite → Apps → Social &amp; ads, then press Sync. Follower counts and weekly growth land here.
        </Empty>
      ) : (
        <>
          <div className="flex items-end justify-between gap-3 border-b border-zinc-800/70 px-4 py-3">
            <div>
              <div className="text-2xl font-semibold tabular-nums text-zinc-50">{num(total)}</div>
              <div className="text-[11px] text-zinc-500">followers across {rows.length} account{rows.length === 1 ? "" : "s"}</div>
            </div>
            <div className={cn("text-sm font-semibold tabular-nums", delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-400" : "text-zinc-500")}>
              {delta > 0 ? "+" : ""}{num(delta)} <span className="text-[11px] font-normal text-zinc-500">this week</span>
            </div>
          </div>
          <ul className="divide-y divide-zinc-800/60">
            {rows.map(({ a, followers, delta: d, series }) => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                <AccountChip a={a} />
                <div className="ml-auto hidden w-24 sm:block">{series.length > 1 && <Spark values={series} className="h-6" />}</div>
                <div className="w-20 text-right">
                  <div className="text-sm font-semibold tabular-nums text-zinc-100">{num(followers)}</div>
                  <div className={cn("text-[11px] tabular-nums", d > 0 ? "text-emerald-400" : d < 0 ? "text-rose-400" : "text-zinc-500")}>{d > 0 ? "+" : ""}{num(d)} this week</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

// ─── Affiliates ──────────────────────────────────────────────────────────────

export function AffiliatesSection({ affiliates, from }: { affiliates: SourceAffiliate[]; from: string }) {
  const [all, setAll] = useState(false);
  const shown = all ? affiliates : affiliates.slice(0, 8);
  const referred = affiliates.reduce((t, a) => t + (Number(a.program?.referred) || 0), 0);
  const earned = affiliates.reduce((t, a) => t + (Number(a.program?.gross_usd) || 0), 0);
  return (
    <Section
      title="Affiliates"
      icon={BadgeDollarSign}
      count={affiliates.length}
      action={<Link to="/affiliates" className="text-xs font-medium text-indigo-300 hover:text-indigo-200">Manage affiliates</Link>}
      bodyClassName="p-0"
    >
      {affiliates.length === 0 ? (
        <Empty icon={BadgeDollarSign} title="No affiliates here yet" action={<Link to="/affiliates" className="text-xs font-medium text-indigo-300 hover:text-indigo-200">Go to Affiliates</Link>}>
          Add partners and affiliates on the Affiliates page. The ones whose relationship and channel match this source show up here.
        </Empty>
      ) : (
        <>
          <div className="flex gap-6 border-b border-zinc-800/70 px-4 py-3 text-xs text-zinc-500">
            <span><span className="text-base font-semibold tabular-nums text-zinc-100">{num(referred)}</span> referred</span>
            <span><span className="text-base font-semibold tabular-nums text-zinc-100">{usd(earned)}</span> commission earned</span>
          </div>
          <ul className="divide-y divide-zinc-800/60">
            {shown.map((a) => {
              const due = a.next_move ? dueLabel(a.next_move) : null;
              return (
                <li key={a.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-4 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
                  <div className="min-w-0">
                    <Link to={`/affiliates/${a.id}`} state={{ from }} className="block truncate text-sm font-medium text-zinc-100 hover:text-indigo-200">{a.name}</Link>
                    <div className="truncate text-[11px] text-zinc-500">
                      {AFFILIATE_KIND[a.kind ?? ""] ?? "Affiliate"} · {AFFILIATE_CHANNEL[a.channel ?? ""] ?? "Our program"}
                      {a.status && a.status !== "active" ? ` · ${a.status.charAt(0).toUpperCase()}${a.status.slice(1)}` : ""}
                    </div>
                  </div>
                  <div className="text-right text-xs tabular-nums text-zinc-400 sm:w-20"><span className="font-semibold text-zinc-100">{num(a.program?.referred ?? 0)}</span> referred</div>
                  <div className={cn("hidden text-right text-sm font-semibold tabular-nums sm:block sm:w-20", Number(a.program?.gross_usd) > 0 ? "text-emerald-300" : "text-zinc-500")} title="Commission earned">{usd(a.program?.gross_usd ?? 0)}</div>
                  <div className={cn(
                    "col-span-2 text-[11px] sm:col-span-1 sm:w-24 sm:text-right",
                    due?.tone === "late" ? "text-rose-400" : due?.tone === "today" ? "text-amber-300" : "text-zinc-500",
                  )}>
                    {due ? `Next: ${due.text}` : "No next move"}
                  </div>
                </li>
              );
            })}
          </ul>
          {affiliates.length > 8 && (
            <div className="border-t border-zinc-800/70 px-4 py-2.5 text-center">
              <Button variant="ghost" size="sm" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${affiliates.length}`}</Button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

